import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { AgGridModule } from 'ag-grid-angular';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { WorkflowRequestsService } from '../../service/entity-services/workflow-requests.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';

import { EntityList } from '../../objects/entity-list';
import { WorkflowRequest } from '../../objects/workflow';

import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';

import * as workflowColumns from '../../shared/entity-columns/workflow-columns';
import { NewWorkflowRequestButtonComponent } from '../../shared/components/new-workflow-request-button/new-workflow-request-button.component';

@Component({
  selector: 'app-workflows-list',
  templateUrl: './workflows-list.component.html',
  styleUrls: ['./workflows-list.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatSidenavModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatButtonToggleModule,
    NewWorkflowRequestButtonComponent
  ]
})
export class WorkflowsListComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private cdr = inject(ChangeDetectorRef);
  public workflowService = inject(WorkflowRequestsService);

  @ViewChild('workflowsSidenavActions') workflowsSidenavActions!: TemplateRef<any>;

  allRequests$: Observable<WorkflowRequest[]> = this.workflowService.allRequests$;

  columnChooserControl = new FormControl();
  customizeColumnsUi = false;
  waitingForServer = false;
  showNameSavedView = false;
  isInitializing = true;

  isViewer = this.permissionsStore.isViewer;

  // Status filter buttons
  statusFilters = ['Pending', 'Requested', 'In Review', 'Approved', 'Signatures', 'Finalized', 'Suspended', 'Canceled'];
  activeStatusFilters: string[] = [];

  // Columns in the requested order
  // colSubmittedTo -> colAssignedTo and colSubmissionDate -> colDateRequested: the
  // originals bound to fields that do not exist on WorkflowRequest and are never
  // returned by the API, so those two columns were permanently blank.
  defaultCols: any[] = [
    workflowColumns.colPriority,
    workflowColumns.colRequestor,
    workflowColumns.colAssignedTo,
    workflowColumns.colPhase,
    workflowColumns.colName,
    workflowColumns.colDocumentType,
    workflowColumns.colDateRequested,
    workflowColumns.colDaysInProcess,
    workflowColumns.colCreatedDate,
    workflowColumns.colDesiredEffectiveDate,
    workflowColumns.colStatus
  ];

  selectedCols = this.defaultCols;

  availableColumns: any[] = [];
  availableColumnSelectList: { value: any; viewValue: string }[] = [];
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{ value: any; viewValue: string }[]>;

  public defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab']
  };
  public showFloatingFilters = false;
  public tooltipShowDelay = 300;
  public tooltipHideDelay = 10000;
  public tooltipInteraction = true;
  public frameworkComponents = {};

  constructor() {
    super();
    this.entityType = 'workflows';
    this.buildAvailableColumns();
    this.initializeSessionStorage();
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Workflows');
    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  override onGridReady(params: any): void {
    super.onGridReady(params);
    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false;
    });
  }

  private async completeInitialization(): Promise<void> {
    this.suspendSessionTracking = true;
    const hasSessionState = this.sessionStorageKey && sessionStorage.getItem(this.sessionStorageKey);
    if (hasSessionState) {
      this.loadSessionState();
      this.suspendSessionTracking = false;
    } else {
      await this.loadUserViews();
      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView = this.savedNamedViews && ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);
      if (hasNewFormatView || hasOldFormatView) {
        await this.loadNamedView(this.savedNamedViews);
        this.suspendSessionTracking = false;
      } else {
        this.autoSizeAll();
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }
    this.isInitializing = false;
    this.cdr.detectChanges();
  }

  private buildAvailableColumns(): void {
    this.availableColumns = [...workflowColumns.allColumns].filter(col => col && col.colId && col.headerName);
    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) uniqueColsMap.set(col.colId, col);
    });
    this.availableColumns = Array.from(uniqueColsMap.values());
    this.availableColumnSelectList = this.availableColumns
      .map(col => ({ value: col, viewValue: col.headerName }))
      .sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  private _filterColumns(value: string | any): { value: any; viewValue: string }[] {
    if (!value || typeof value !== 'string') return this.availableColumnSelectList;
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option => option.viewValue.toLowerCase().includes(filterValue));
  }

  displayColumnFn(): string { return ''; }

  ngAfterViewInit(): void {
    this.sidenavContentService.updateSidenavContent(this.workflowsSidenavActions);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.updateSidenavContent(null);
  }

  // Status filter toggle
  toggleStatusFilter(status: string): void {
    const index = this.activeStatusFilters.indexOf(status);
    if (index >= 0) {
      this.activeStatusFilters.splice(index, 1);
    } else {
      this.activeStatusFilters.push(status);
    }
    this.applyStatusFilter();
  }

  isStatusActive(status: string): boolean {
    return this.activeStatusFilters.includes(status);
  }

  private applyStatusFilter(): void {
    if (!this.gridApi) return;

    if (this.activeStatusFilters.length === 0) {
      this.gridApi.setGridOption('isExternalFilterPresent', () => false);
      this.gridApi.setGridOption('doesExternalFilterPass', () => true);
    } else {
      this.gridApi.setGridOption('isExternalFilterPresent', () => true);
      this.gridApi.setGridOption('doesExternalFilterPass', (node: any) => {
        const status = node.data?.status;
        return this.activeStatusFilters.includes(status);
      });
    }
    this.gridApi.onFilterChanged();
  }

  // Sidebar actions
  createNewRequest(): void {
    this.router.navigateByUrl('/securehome/new-workflow');
  }

  showNotImplemented(featureName: string): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: featureName,
        message: 'Not Implemented On Your Deployment',
        confirmLabel: 'OK',
        cancelLabel: '',
        icon: 'info'
      }
    });
  }

  onCellClicked(event: any): void {
    if (event.data?.workflowNum) {
      this.router.navigate(['/securehome/workflow', event.data.workflowNum]);
    }
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) this.gridApi.setGridOption('quickFilterText', searchText);
  }

  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      setTimeout(() => this.columnSelectorControl.setValue('', { emitEvent: false }), 0);
    }
  }

  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) this.selectedColumnsList.splice(index, 1);
  }

  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;
      this.markSessionAsModified();
    }
  }

  openCustomizeColumnsDialog(): void {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState.filter((col: any) => !col.hide).map((col: any) => col.colId);
      this.selectedColumnsList = this.availableColumns.filter(col => visibleColIds.includes(col.colId));
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }
    this.customizeColumnsUi = true;
  }

  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;
    if (this.gridApi) {
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => allColumnIds.push(column.getId()));
      allColumnIds.forEach(colId => this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters));
      this.gridApi.refreshHeader();
    }
  }

  exportCSV(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({ processCellCallback: this.formattingFunction, fileName: 'workflows_export.csv' });
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsExcel({ processCellCallback: this.formattingFunction, fileName: 'workflows_export.xlsx' });
    }
  }
}
